import axios from 'axios';

// Base URL del backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Crear instancia de Axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a cada request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Error con respuesta del servidor
      const { status, data } = error.response;

      if (status === 401) {
        // Token inválido o expirado
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }

      // Retornar error con mensaje formateado
      return Promise.reject({
        status,
        message: data.detail || data.message || 'Error en la solicitud',
        data,
      });
    } else if (error.request) {
      // Request sin respuesta (problema de red)
      return Promise.reject({
        status: 0,
        message: 'No se pudo conectar con el servidor',
      });
    } else {
      // Error al configurar request
      return Promise.reject({
        status: 0,
        message: error.message || 'Error inesperado',
      });
    }
  }
);

export default api;

// Funciones helper para endpoints específicos
export const authAPI = {
  login: (credentials) => {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    return api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

export const usuariosAPI = {
  getAll: (params) => api.get('/usuarios', { params }),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
};

export const organizacionesAPI = {
  getAll: (params) => api.get('/organizaciones', { params }),
  getById: (id) => api.get(`/organizaciones/${id}`),
  create: (data) => api.post('/organizaciones', data),
  update: (id, data) => api.put(`/organizaciones/${id}`, data),
  delete: (id) => api.delete(`/organizaciones/${id}`),
  getUsuarios: (id) => api.get(`/organizaciones/${id}/usuarios`),
  getEstadisticas: (id) => api.get(`/organizaciones/${id}/estadisticas`),
};

export const glosarioAPI = {
  getAll: (params) => api.get('/glosario', { params }),
  buscar: (query) => api.get('/glosario/buscar', { params: { query } }),
  getCategorias: () => api.get('/glosario/categorias'),
  getById: (id) => api.get(`/glosario/${id}`),
  create: (data) => api.post('/glosario', data),
  update: (id, data) => api.put(`/glosario/${id}`, data),
  delete: (id) => api.delete(`/glosario/${id}`),
};

export const chequeoAPI = {
  getPlantilla: () => api.get('/chequeo/plantilla'),
  getEvaluaciones: (params) => api.get('/chequeo/evaluaciones', { params }),
  createEvaluacion: (data) => api.post('/chequeo/evaluaciones', data),
  getEvaluacion: (id) => api.get(`/chequeo/evaluaciones/${id}`),
  updateEvaluacion: (id, data) => api.put(`/chequeo/evaluaciones/${id}`, data),
  guardarRespuestasBatch: (id, respuestas) => 
    api.post(`/chequeo/evaluaciones/${id}/respuestas/batch`, { respuestas }),
  getAnalisis: (id) => api.get(`/chequeo/evaluaciones/${id}/analisis`),
  getCompletitud: (id) => api.get(`/chequeo/evaluaciones/${id}/completitud`),
};

export const encuestasAPI = {
  getPlantilla: () => api.get('/encuestas/plantilla'),
  createPreguntaPlantilla: (data) => api.post('/encuestas/plantilla', data),
  updatePreguntaPlantilla: (id, data) => api.put(`/encuestas/plantilla/${id}`, data),
  deletePreguntaPlantilla: (id) => api.delete(`/encuestas/plantilla/${id}`),
  createOpcion: (idPregunta, data) => api.post(`/encuestas/plantilla/${idPregunta}/opciones`, data),
  updateOpcion: (idOpcion, data) => api.put(`/encuestas/plantilla/opciones/${idOpcion}`, data),
  deleteOpcion: (idOpcion) => api.delete(`/encuestas/plantilla/opciones/${idOpcion}`),
  getInstancias: (params) => api.get('/encuestas/instancias', { params }),
  createInstancia: (data) => api.post('/encuestas/instancias', data),
  getInstancia: (id) => api.get(`/encuestas/instancias/${id}`),
  updateInstancia: (id, data) => api.put(`/encuestas/instancias/${id}`, data),
  guardarRespuestasBatch: (id, respuestas) =>
    api.post(`/encuestas/instancias/${id}/respuestas/batch`, { respuestas }),
  getParticipacion: (id) => api.get(`/encuestas/instancias/${id}/participacion`),
  getAnalisis: (id) => api.get(`/encuestas/instancias/${id}/analisis`),
};

export const codigosAPI = {
  getAll: (params) => api.get('/codigos-etica', { params }),
  getById: (id) => api.get(`/codigos-etica/${id}`),
  create: (data) => api.post('/codigos-etica', data),
  update: (id, data) => api.put(`/codigos-etica/${id}`, data),
  generar: (data, guardar = true) => 
    api.post(`/codigos-etica/generar?guardar=${guardar}`, data),
  aprobar: (id, data) => api.post(`/codigos-etica/${id}/aprobar`, data),
  cambiarEstado: (id, data) => api.put(`/codigos-etica/${id}/estado`, data),
  getHistorial: (idOrg) => api.get(`/codigos-etica/organizacion/${idOrg}/historial`),
  getEstadisticas: (idOrg) => api.get(`/codigos-etica/organizacion/${idOrg}/estadisticas`),
};

export const comiteAPI = {
  getMiembros: (params) => api.get('/comite-etica/miembros', { params }),
  getMiembro: (id) => api.get(`/comite-etica/miembros/${id}`),
  createMiembro: (data) => api.post('/comite-etica/miembros', data),
  updateMiembro: (id, data) => api.put(`/comite-etica/miembros/${id}`, data),
  deleteMiembro: (id) => api.delete(`/comite-etica/miembros/${id}`),
  cambiarRol: (id, data) => api.post(`/comite-etica/miembros/${id}/cambiar-rol`, data),
  desactivar: (id, data) => api.post(`/comite-etica/miembros/${id}/desactivar`, data),
  getComposicion: (idOrg) => api.get(`/comite-etica/organizacion/${idOrg}/composicion`),
  getEstadisticas: (idOrg) => api.get(`/comite-etica/organizacion/${idOrg}/estadisticas`),
};

export const denunciasAPI = {
  getAll: (params) => api.get('/denuncias', { params }),
  getById: (id) => api.get(`/denuncias/${id}`),
  create: (data) => api.post('/denuncias', data),
  update: (id, data) => api.put(`/denuncias/${id}`, data),
  asignar: (id, data) => api.post(`/denuncias/${id}/asignar`, data),
  cambiarEstado: (id, data) => api.put(`/denuncias/${id}/estado`, data),
  resolver: (id, data) => api.post(`/denuncias/${id}/resolver`, data),
  getSeguimientos: (id) => api.get(`/denuncias/${id}/seguimiento`),
  addSeguimiento: (id, data) => api.post(`/denuncias/${id}/seguimiento`, data),
  getEstadisticas: (idOrg) => api.get(`/denuncias/organizacion/${idOrg}/estadisticas`),
  getResumen: (idOrg) => api.get(`/denuncias/organizacion/${idOrg}/resumen`),
};
